/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ContextualMenu, DirectionalHint, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { IIconProps, IIconStyles, Icon } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useDebouncedState } from 'src/core/hooks/useDebouncedState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { HealthTokenStore } from 'src/languageStudio/appShell/prebuiltDemos/taForHealth/HealthTokenStore';
import { HealthCareAssertion } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { tokenDataAttribute, tokenIndexDataAttribute } from 'src/modules/labeling/utils/labelingConstants';
import { ITokenRendererProps } from 'src/packages/react-text-annotator/lib/esm';
import styled from 'styled-components';

const { styleDiv, styleSpan } = getStylistV2('HealthTokenRenderer');
const theme = getScopedTheme('HealthTokenRenderer');

const messages = defineMessages({
    assertions: { id: 'languageStudio.healthTokenRenderer.assertions', defaultMessage: 'Assertions:' }
});

const LinksContainer = styleDiv('LinksContainer', {
    top: 2,
    width: 130,
    marginLeft: 5,
    borderRadius: 2,
    cursor: 'pointer',
    position: 'relative',
    display: 'inline-block',
    height: theme.customTokenHeight,
    backgroundColor: theme.dropdownBackgroundColor
});

const StyledStack = styled(Stack)({
    top: 2,
    marginLeft: 5,
    borderRadius: 2,
    position: 'relative',
    display: 'inline-block',
    height: theme.customTokenHeight,
    backgroundColor: theme.dropdownBackgroundColor
});

const StyledText = styled(LuisText)({
    width: '100%',
    paddingLeft: 5,
    paddingRight: 22,
    overflow: 'hidden',
    boxSizing: 'border-box',
    fontSize: '12px',
    display: 'inline-block',
    textOverflow: 'ellipsis',
    overflowWrap: 'anywhere',
    fontFamily: theme.textFontFamily
});

const LinkTitle = styleSpan('LinkTitle', {
    width: '100%',
    paddingLeft: 5,
    paddingRight: 22,
    overflow: 'hidden',
    boxSizing: 'border-box',
    display: 'inline-block',
    textOverflow: 'ellipsis',
    height: theme.customTokenHeight,
    fontFamily: theme.textFontFamily,
    lineHeight: `${theme.customTokenHeight}px`
});

const iconStyles = classNamesFunction<IIconProps, IIconStyles>()({
    root: { lineHeight: `${theme.customTokenHeight}px`, position: 'absolute', right: 4 }
});

type HealthTokenRendererProps = ITokenRendererProps<HealthTokenStore> & { assertions?: HealthCareAssertion };

export const HealthTokenRenderer = observer((props: HealthTokenRendererProps) => {
    const { tokenStore, assertions } = props;
    const linkContainerRef = React.useRef<HTMLDivElement>();
    const [isMenuVisible, setIsMenuVisible] = useDebouncedState<boolean>(false, 100);

    const { localize } = useLocalization();

    const tokenDataAttrObj = { [tokenDataAttribute]: true, [tokenIndexDataAttribute]: `${tokenStore.index}` };
    const linkOptions: IContextualMenuItem[] = tokenStore.entityLinks.map(l => ({
        key: l.id,
        text: l.text,
        canCheck: true,
        checked: l.id === tokenStore.selectedLinkId
    }));

    const onItemClick = (item: IContextualMenuItem) => {
        tokenStore.setSelectedLink(item.key);
        setIsMenuVisible(false);
    };

    const assertionsString: string = React.useMemo(() => {
        const assertionsArray: string[] = [];

        if (assertions?.association !== undefined) {
            assertionsArray.push(assertions?.association);
        }
        if (assertions?.certainty !== undefined) {
            assertionsArray.push(assertions?.certainty);
        }
        if (assertions?.conditionality !== undefined) {
            assertionsArray.push(assertions?.conditionality);
        }

        return assertionsArray.join(', ');
    }, [assertions]);

    return (
        <span className="inner-token">
            <span {...tokenDataAttrObj}>
                {tokenStore.text}

                {linkOptions.length > 0 && (
                    <>
                        <LinksContainer
                            originalRef={linkContainerRef}
                            onMouseEnter={() => setIsMenuVisible(true)}
                            onMouseLeave={() => setIsMenuVisible(false)}>
                            <LinkTitle data-automation-id="selectedLink">{tokenStore.visibleEntityLink?.text}</LinkTitle>
                            <Icon iconName="ChevronDown" styles={iconStyles} />
                        </LinksContainer>

                        <ContextualMenu
                            alignTargetEdge
                            items={linkOptions}
                            isBeakVisible={false}
                            hidden={!isMenuVisible}
                            target={linkContainerRef}
                            shouldFocusOnContainer={false}
                            onDismiss={() => setIsMenuVisible(false)}
                            onItemClick={(e, item) => {
                                e.preventDefault();
                                onItemClick(item);
                            }}
                            directionalHint={DirectionalHint.bottomLeftEdge}
                            calloutProps={{
                                calloutMaxHeight: 300,
                                onMouseEnter: () => setIsMenuVisible(true),
                                onMouseLeave: () => setIsMenuVisible(false)
                            }}
                        />
                    </>
                )}

                {assertions && (
                    <StyledStack>
                        <StyledText color={theme.secondaryTextColor}>{localize(messages.assertions)}</StyledText>
                        <br />
                        <StyledText lineHeight={theme.assertionsLineHeight}>{assertionsString}</StyledText>
                    </StyledStack>
                )}
            </span>
        </span>
    );
});
